import Search from "@/assets/icons/Search";
import BadgeShared from "@/components/shared/Badge/Badge";
import CardCurrency from "@/components/shared/CardCurrency/CardCurrency";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableCell } from "@/components/ui/table";
import LayoutInside from "@/layout/LayoutInside";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Search as SearchIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { InfoTooltip } from "./components/InfoTooltip";
import { RefundRequestDetailSheet } from "./components/RefundRequestDetailSheet";
import {
  REFUND_REQUEST_STATUS_TABS,
  useRefundRequests,
  useRefundRequestsSummary,
} from "./hooks/useRefundRequests";
import {
  refundRequestAction,
  refundRequestBadgeStatus,
  refundRequestWarrantyState,
} from "./refund-request.utils";

export default function Disputes() {
  const { t } = useTranslation(["common", "disputes", "routes"]);
  const { data: summary, isLoading: isLoadingSummary } = useRefundRequestsSummary();
  const {
    requests,
    tableData,
    total,
    isLoading,
    currentPage,
    setCurrentPage,
    searchQuery,
    handleSearchChange,
    activeTab,
    setStatusTab,
    selectedId,
    openRequest,
    closeRequest,
  } = useRefundRequests();

  const pending = summary?.pending ?? 0;
  const awaitingSeller = summary?.awaiting_seller ?? 0;
  const inMediation = summary?.in_mediation ?? 0;
  const deadlineEnding = summary?.deadline_ending ?? 0;
  const deadlineHours = summary?.deadline_ending_hours ?? 24;

  const tabCount = {
    all: summary?.total,
    pending: summary?.pending,
    mediation: summary?.in_mediation,
    refunding: summary?.refunding,
    closed: summary?.closed,
  };

  const breadcrumbItems = [
    { text: t("routes:sales"), link: "/sales" },
    { text: t("disputes:title"), link: "/sales/disputes" },
  ];

  const headers = [
    { title: t("disputes:table.sale"), key: "sale" },
    { title: t("disputes:table.reason"), key: "reason" },
    { title: t("disputes:table.amount"), key: "amount" },
    {
      title: (
        <span className="inline-flex items-center gap-1">
          {t("disputes:table.warranty")}
          <InfoTooltip text={t("disputes:warranty_tooltip")} />
        </span>
      ),
      key: "warranty",
    },
    { title: t("disputes:table.status"), key: "status" },
    { title: "", key: "actions" },
  ];

  return (
    <LayoutInside items={breadcrumbItems}>
      <RefundRequestDetailSheet requestId={selectedId} onClose={closeRequest} />

      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">{t("disputes:title")}</h1>
          <p className="text-sm text-muted-foreground">{t("disputes:subtitle")}</p>
        </header>

        <Card className="p-4 space-y-3">
          <div role="tablist" className="flex flex-wrap items-center gap-2">
            <Button role="tab" aria-selected size="sm" className="gap-2 shadow-md">
              <span>{t("disputes:types.refunds")}</span>
              {awaitingSeller > 0 && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">{awaitingSeller}</span>
              )}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{t("disputes:types.refunds_description")}</p>
        </Card>

        {deadlineEnding > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            <Clock size={16} className="shrink-0" />
            <span>
              {t("disputes:deadline_ending_alert", { count: deadlineEnding, hours: deadlineHours })}
            </span>
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <CardCurrency
            text={t("disputes:cards.requested_amount")}
            value={summary?.requested_amount_formatted ?? "—"}
            subtitle={t("disputes:cards.requested_amount_hint")}
            status="period"
            isLoading={isLoadingSummary}
          />
          <div onClick={() => setStatusTab("pending")} className="cursor-pointer">
            <CardCurrency
              text={t("disputes:cards.pending")}
              value={String(pending)}
              subtitle={t("disputes:cards.pending_hint")}
              status="pending"
              isLoading={isLoadingSummary}
            />
          </div>
          <div onClick={() => setStatusTab("mediation")} className="cursor-pointer">
            <CardCurrency
              text={t("disputes:cards.mediation")}
              value={String(inMediation)}
              subtitle={t("disputes:cards.mediation_hint")}
              status="awaiting_payment"
              isLoading={isLoadingSummary}
            />
          </div>
          <CardCurrency
            text={t("disputes:cards.deadline_ending")}
            value={String(deadlineEnding)}
            subtitle={t("disputes:cards.deadline_ending_hint", { hours: deadlineHours })}
            status="not_authorized"
            isLoading={isLoadingSummary}
          />
        </section>

        <Card className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {REFUND_REQUEST_STATUS_TABS.map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? "default" : "ghost"}
                size="sm"
                onClick={() => setStatusTab(tab)}
                className={cn("gap-2 transition-all", activeTab === tab && "shadow-md")}
              >
                <span>{t(`disputes:tabs.${tab}`)}</span>
                {typeof tabCount[tab] === "number" && (
                  <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                    {tabCount[tab]}
                  </span>
                )}
              </Button>
            ))}
          </div>

          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("disputes:search_placeholder")}
              className="pl-9 w-full"
            />
          </div>
        </Card>

        <Card className="w-full overflow-x-auto bg-[#18181B]">
          <div className="p-4 flex items-center justify-between gap-4">
            <h2 className="text-lg font-medium">{t("disputes:types.refunds")}</h2>
            <span className="text-sm text-muted-foreground">
              {t("disputes:table.count", { count: total })}
            </span>
          </div>
          <TableListCustom
            headers={headers}
            isLoadingData={isLoading}
            data={tableData}
            currentPage={currentPage}
            datacy="refund-requests-table"
            setCurrentPage={setCurrentPage}
            emptyIconMessage={<Search />}
            emptyTitleMessage={t("disputes:empty_title")}
            emptyDescriptionMessage={t("disputes:empty_description")}
          >
            <AnimatePresence mode="wait" initial={false}>
              {requests.map((request, index) => {
                const warrantyState = refundRequestWarrantyState(request);
                const action = refundRequestAction(request);

                return (
                  <motion.tr
                    key={request.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    className="h-16 text-sm border-t border-zinc-800 transition-colors hover:bg-secondary/50 cursor-pointer"
                    onClick={() => openRequest(request.id)}
                  >
                    <TableCell className="max-w-[170px] py-4">
                      <div className="font-medium truncate" title={request.sale.product ?? undefined}>
                        {request.sale.product ?? "—"}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {request.sale.buyer_name ?? "—"} · {request.code}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[130px] whitespace-normal text-zinc-300">
                      <span className="line-clamp-2">{request.reason ?? "—"}</span>
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">
                      {request.amount_formatted}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "whitespace-nowrap",
                        warrantyState === "ending" && "text-red-500 font-semibold",
                        warrantyState === "open" && "font-medium",
                        (warrantyState === "closed" || warrantyState === "expired") && "text-muted-foreground",
                      )}
                    >
                      {warrantyState === "closed"
                        ? "—"
                        : warrantyState === "expired"
                          ? t("disputes:warranty_expired")
                          : t("disputes:days_left", { count: request.warranty_days_left ?? 0 })}
                    </TableCell>
                    <TableCell>
                      <BadgeShared
                        status={refundRequestBadgeStatus(request.status)}
                        text={t(`disputes:status.${request.status}`)}
                      />
                    </TableCell>
                    <TableCell
                      className="sticky right-0 bg-background"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="sm"
                        variant={action === "details" ? "ghost" : "default"}
                        onClick={() => openRequest(request.id)}
                      >
                        {t(`disputes:actions.${action}`)}
                      </Button>
                    </TableCell>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </TableListCustom>
        </Card>
      </motion.section>
    </LayoutInside>
  );
}
